import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { ChevronDown, TrendingUp, TrendingDown, Target, Info, Utensils, Calculator } from 'lucide-react';
const data = [{
  name: 'Week 1',
  cost: 21.5,
  plan: 22
}, {
  name: 'Week 2',
  cost: 23.2,
  plan: 22
}, {
  name: 'Week 3',
  cost: 20.8,
  plan: 22
}, {
  name: 'Week 4',
  cost: 22.1,
  plan: 22
}, {
  name: 'Week 5',
  cost: 21.9,
  plan: 22
}];
export function FoodCostView() {
  const [selectedWeek, setSelectedWeek] = useState('Week 1');
  return <div className="space-y-6" data-id="element-210">
      <div className="flex items-center justify-between" data-id="element-211">
        <div data-id="element-212">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-213">
            Food Cost Calculator
          </h2>
          <p className="text-sm text-slate-500" data-id="element-214">
            Detailed food cost analysis and target tracking
          </p>
        </div>
        <div className="relative" data-id="element-215">
          <select value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)} className="appearance-none pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all cursor-pointer shadow-sm" data-id="element-216">
            <option data-id="element-217">Week 1</option>
            <option data-id="element-218">Week 2</option>
            <option data-id="element-219">Week 3</option>
            <option data-id="element-220">Week 4</option>
            <option data-id="element-221">Week 5</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} data-id="element-222" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-223">
        {/* Food Cost Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-224">
          <div className="p-6 border-b border-slate-50 flex items-center justify-between" data-id="element-225">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-226">
              Food Cost Metrics
            </h3>
            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-bold uppercase" data-id="element-227">
              <Utensils size={12} data-id="element-228" />
              Live Data
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-229">
            <table className="w-full text-left" data-id="element-230">
              <thead data-id="element-231">
                <tr className="bg-slate-50/50" data-id="element-232">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-233">
                    Category
                  </th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-234">
                    Actual Cost
                  </th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-235">
                    Theoretical
                  </th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-236">
                    Variance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-237">
                <MetricRow label="Meat & Poultry" actual="$4,250" theoretical="$4,100" variance="+3.6%" isNegative data-id="element-238" />
                <MetricRow label="Seafood" actual="$2,800" theoretical="$2,950" variance="-5.1%" isPositive data-id="element-239" />
                <MetricRow label="Produce" actual="$1,450" theoretical="$1,400" variance="+3.5%" isNegative data-id="element-240" />
                <MetricRow label="Dairy" actual="$980" theoretical="$1,000" variance="-2.0%" isPositive data-id="element-241" />
                <MetricRow label="Dry Goods" actual="$1,200" theoretical="$1,150" variance="+4.3%" isNegative data-id="element-242" />
              </tbody>
            </table>
          </div>
        </div>

        {/* Target Card */}
        <div className="bg-emerald-600 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden" data-id="element-243">
          <div className="relative z-10" data-id="element-244">
            <div className="flex items-center gap-2 mb-8" data-id="element-245">
              <Target className="text-emerald-200" size={20} data-id="element-246" />
              <h3 className="text-sm font-bold uppercase tracking-wider" data-id="element-247">
                Target Performance
              </h3>
            </div>
            <div className="text-center py-4" data-id="element-248">
              <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest mb-1" data-id="element-249">
                Current Food Cost %
              </p>
              <p className="text-5xl font-black" data-id="element-250">21.8%</p>
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-xs font-bold" data-id="element-251">
                <TrendingDown size={14} data-id="element-252" />
                0.2% Below Plan
              </div>
            </div>
            <div className="mt-8 p-4 bg-black/10 rounded-xl border border-white/10" data-id="element-253">
              <p className="text-[10px] text-emerald-50 leading-relaxed font-medium" data-id="element-254">
                Excellent! You are currently maintaining a food cost percentage
                below the 22% target. Keep monitoring high-variance items like
                Meat and Produce.
              </p>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-white/10 rounded-full blur-3xl" data-id="element-255" />
        </div>

        {/* Visual Chart */}
        <div className="lg:col-span-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-256">
          <div className="flex items-center justify-between mb-8" data-id="element-257">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-258">
              Weekly Trend vs. 22% Target
            </h3>
            <div className="flex gap-4" data-id="element-259">
              <div className="flex items-center gap-1.5" data-id="element-260">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" data-id="element-261" />
                <span className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-262">
                  On Target
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-263">
                <div className="w-2 h-2 bg-rose-500 rounded-full" data-id="element-264" />
                <span className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-265">
                  Over Target
                </span>
              </div>
            </div>
          </div>
          <div className="h-80 w-full" data-id="element-266">
            <ResponsiveContainer width="100%" height="100%" data-id="element-267">
              <BarChart data={data} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-268">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" data-id="element-269" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fontWeight: 700,
                fill: '#94A3B8'
              }} dy={10} data-id="element-270" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fontWeight: 700,
                fill: '#94A3B8'
              }} tickFormatter={val => `${val}%`} data-id="element-271" />
                <Tooltip cursor={{
                fill: '#F8FAFC'
              }} contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
              }} data-id="element-272" />
                <ReferenceLine y={22} stroke="#10B981" strokeDasharray="5 5" label={{
                position: 'right',
                value: 'Target (22%)',
                fill: '#10B981',
                fontSize: 10,
                fontWeight: 800
              }} data-id="element-273" />
                <Bar dataKey="cost" radius={[6, 6, 0, 0]} barSize={40} data-id="element-274">
                  {data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.cost > 22 ? '#F43F5E' : '#10B981'} data-id="element-275" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}
function MetricRow({
  label,
  actual,
  theoretical,
  variance,
  isPositive,
  isNegative
}: any) {
  return <tr className="hover:bg-slate-50 transition-colors" data-id="element-276">
      <td className="px-6 py-4 text-sm font-bold text-slate-700" data-id="element-277">{label}</td>
      <td className="px-6 py-4 text-sm font-black text-slate-900" data-id="element-278">{actual}</td>
      <td className="px-6 py-4 text-sm font-black text-slate-900" data-id="element-279">
        {theoretical}
      </td>
      <td className="px-6 py-4" data-id="element-280">
        <div className={`flex items-center gap-1 text-xs font-bold ${isPositive ? 'text-emerald-600' : isNegative ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-281">
          {isPositive ? <TrendingDown size={14} data-id="element-282" /> : isNegative ? <TrendingUp size={14} data-id="element-283" /> : null}
          {variance}
        </div>
      </td>
    </tr>;
}